'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ProductSpecsHoverCardProps = {
  name: string;
  price: number;
  originalPrice?: number | null;
  stock?: number | null;
  fullSpecs?: Record<string, string> | null;
  specsText?: string | null;
};

type ParsedSpecEntry = {
  label?: string;
  value: string;
};

function formatPrice(value: number) {
  return `${new Intl.NumberFormat('vi-VN').format(Number(value || 0))}₫`;
}

function parseSpecs(text?: string | null): ParsedSpecEntry[] {
  if (!text) return [];
  return text
    .split(/\r?\n|,|;|\||•/g)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((value) => ({ value }))
    .slice(0, 5);
}

function parseFullSpecs(fullSpecs?: Record<string, string> | null): ParsedSpecEntry[] {
  if (!fullSpecs || typeof fullSpecs !== 'object') return [];
  return Object.entries(fullSpecs)
    .map(([key, value]) => ({
      label: cleanSpecLabel(String(key).trim()),
      value: String(value).trim(),
    }))
    .filter((entry) => entry.value)
    .slice(0, 6);
}

function cleanSpecLabel(label: string) {
  return label
    .replace(/\s+/g, ' ')
    .replace(/^(Thông tin chung|Thông số kỹ thuật|Thông số sản phẩm)\s*[-:]\s*/i, '')
    .trim();
}

export default function ProductSpecsHoverCard({
  name,
  price,
  originalPrice,
  stock,
  fullSpecs,
  specsText,
}: ProductSpecsHoverCardProps) {
  const specs = useMemo(() => {
    const detailed = parseFullSpecs(fullSpecs);
    return detailed.length ? detailed : parseSpecs(specsText);
  }, [fullSpecs, specsText]);
  const listPrice = Number(originalPrice || 0);
  const salePrice = Number(price || 0);
  const percent =
    listPrice > salePrice && listPrice > 0
      ? Math.round(((listPrice - salePrice) / listPrice) * 100)
      : 0;
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const hoveringRef = useRef(false);
  const visibleRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const setPopupVisible = useCallback((nextVisible: boolean) => {
    if (visibleRef.current === nextVisible) return;
    visibleRef.current = nextVisible;
    setVisible(nextVisible);
  }, []);

  const updatePosition = useCallback((clientX?: number, clientY?: number) => {
    if (!popupRef.current) return;
    const trigger = triggerRef.current?.parentElement;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const mouseX = typeof clientX === 'number' ? clientX : rect.left + rect.width / 2;
    const mouseY = typeof clientY === 'number' ? clientY : rect.top + rect.height / 2;

    const popupWidth = 470;
    const popupHeight = popupRef.current.offsetHeight || 340;
    const viewportPadding = 12;
    const cursorGap = 18;

    let left = mouseX + cursorGap;
    let top = mouseY - Math.min(72, popupHeight * 0.22);

    const maxLeft = window.innerWidth - popupWidth - viewportPadding;
    if (left > maxLeft) left = mouseX - popupWidth - cursorGap;
    if (left < viewportPadding) left = viewportPadding;

    const maxTop = window.innerHeight - popupHeight - viewportPadding;
    if (top > maxTop) top = maxTop;
    if (top < viewportPadding) top = viewportPadding;

    const overlapsCursorX = mouseX >= left - 2 && mouseX <= left + popupWidth + 2;
    const overlapsCursorY = mouseY >= top - 2 && mouseY <= top + popupHeight + 2;
    if (overlapsCursorX && overlapsCursorY) {
      const rightSpace = window.innerWidth - mouseX;
      const leftSpace = mouseX;
      left =
        rightSpace >= leftSpace
          ? Math.min(maxLeft, mouseX + cursorGap)
          : Math.max(viewportPadding, mouseX - popupWidth - cursorGap);
    }

    popupRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  }, []);

  const schedulePosition = useCallback(
    (clientX: number, clientY: number) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        updatePosition(clientX, clientY);
        setPopupVisible(true);
      });
    },
    [setPopupVisible, updatePosition]
  );

  const handlePointerEnter = useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (event.pointerType === 'touch') return;
      hoveringRef.current = true;
      setPopupVisible(true);
      requestAnimationFrame(() => schedulePosition(event.clientX, event.clientY));
    },
    [schedulePosition, setPopupVisible]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (!hoveringRef.current || event.pointerType === 'touch') return;
      if (
        document.documentElement.classList.contains('is-scrolling') ||
        document.documentElement.classList.contains('is-dragging')
      ) {
        setPopupVisible(false);
        return;
      }
      schedulePosition(event.clientX, event.clientY);
    },
    [schedulePosition, setPopupVisible]
  );

  const handlePointerLeave = useCallback(() => {
    hoveringRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setPopupVisible(false);
  }, [setPopupVisible]);

  useEffect(
    () => () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!visible) return;

    const handleHide = () => setPopupVisible(false);
    const handleResize = () => setPopupVisible(false);

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleHide, { passive: true });
    window.addEventListener('wheel', handleHide, { passive: true });
    window.addEventListener('touchmove', handleHide, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleHide);
      window.removeEventListener('wheel', handleHide);
      window.removeEventListener('touchmove', handleHide);
    };
  }, [setPopupVisible, visible]);

  const popup = (
    <div
      ref={popupRef}
      className={`product-specs-hover-card pointer-events-none fixed z-[12000] hidden w-[470px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.28)] ring-1 ring-black/5 transition-[opacity,filter] duration-150 md:block ${
        visible ? 'visible opacity-100 blur-0' : 'invisible opacity-0 blur-[1px]'
      }`}
      style={{ transform: 'translate3d(0, 0, 0)' }}
    >
      <div className="relative overflow-hidden bg-gradient-to-r from-[#d97706] via-[#f2b400] to-[#ea580c] px-4 py-4 text-white">
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/12" />
        <div className="absolute -bottom-12 left-10 h-24 w-24 rounded-full bg-amber-300/18" />
        <div className="relative line-clamp-3 text-[18px] font-black uppercase leading-snug tracking-[-0.02em] drop-shadow-sm">
          {name}
        </div>
      </div>

      <div className="grid grid-cols-[112px_1fr] gap-x-4 gap-y-2 px-5 py-4 text-[14px]">
        <div className="font-extrabold text-slate-900">Giá bán</div>
        <div>
          <span className="text-[22px] font-black leading-none tracking-[-0.03em] text-[#d97706]">
            {formatPrice(salePrice)}
          </span>
          {listPrice > salePrice ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-slate-400 line-through">{formatPrice(listPrice)}</span>
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-black text-orange-700 ring-1 ring-orange-100">
                -{percent}%
              </span>
            </div>
          ) : null}
        </div>

        <div className="font-extrabold text-slate-900">Tình trạng</div>
        <div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-black ${
              Number(stock || 0) > 0
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                : 'bg-red-50 text-red-700 ring-1 ring-red-100'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                Number(stock || 0) > 0 ? 'bg-emerald-500' : 'bg-red-500'
              }`}
            />
            {Number(stock || 0) > 0 ? 'Còn hàng' : 'Hết hàng'}
          </span>
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="overflow-hidden rounded-2xl border border-amber-300/70 bg-white">
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#d97706] via-[#f2b400] to-[#ea580c] px-4 py-3 text-white shadow-sm">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.4}
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />
              </svg>
            </span>
            <div className="text-[17px] font-black">Thông số sản phẩm</div>
          </div>

          {specs.length ? (
            <ul className="divide-y divide-slate-100 bg-white px-3 py-2 text-[14px] text-slate-800">
              {specs.map((spec, i) => (
                <li
                  key={i}
                  className="grid grid-cols-[18px_minmax(135px,165px)_1fr] gap-2 px-1 py-2.5 align-top"
                >
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#d6a000]" />
                  {spec.label ? (
                    <span className="text-[13px] font-extrabold leading-snug text-slate-600 break-words">
                      {spec.label}
                    </span>
                  ) : (
                    <span className="hidden" />
                  )}
                  <span
                    className={`min-w-0 break-words [overflow-wrap:anywhere] leading-snug text-slate-900 ${
                      spec.label ? '' : 'col-span-2'
                    }`}
                  >
                    {spec.value}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 text-[14px] italic text-slate-500">
              Chưa có thông số chi tiết.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const [shouldRender, setShouldRender] = useState(false);
  useEffect(() => {
    if (visible) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 180);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  return (
    <>
      <span
        ref={triggerRef}
        className="absolute inset-0 hidden md:block"
        aria-hidden="true"
        onPointerEnter={handlePointerEnter}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      />
      {mounted && shouldRender ? createPortal(popup, document.body) : null}
      <style jsx global>{`
        /* Shimmer removed for performance */
      `}</style>
    </>
  );
}
